import React from 'react';
import { useTokens } from '../../../core/theme/TokensProvider';
import { Text } from '../../../ui/primitives/Text';
import { CheckinHeatmapCell } from '../../../model/members/CheckinReport';

// ISO day-of-week: 1 = Monday .. 7 = Sunday (matches the server's EXTRACT(ISODOW ...)).
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const HOURS = Array.from({ length: 24 }, (_, hour) => hour);
// Label every third hour — 24 labels don't fit legibly at this cell size.
const LABELLED_HOURS = new Set([0, 3, 6, 9, 12, 15, 18, 21]);

interface CheckinHeatmapProps {
    cells: CheckinHeatmapCell[];
}

/**
 * FORT-345: day-of-week x hour-of-day activity grid. Shows the interaction two separate
 * 1-D charts can't — e.g. "Tuesday 18:00 is the crunch" — for staffing decisions.
 * Buckets are computed server-side in the club's timezone.
 */
const CheckinHeatmap: React.FC<CheckinHeatmapProps> = ({ cells }) => {
    const tokens = useTokens();

    const counts = new Map<string, number>();
    cells.forEach(c => counts.set(`${c.dayOfWeek}-${c.hour}`, c.count));
    const max = cells.reduce((highest, c) => Math.max(highest, c.count), 0);

    // Keep the faintest non-zero cell visible rather than fading it to nothing.
    const intensity = (count: number) => (count === 0 || max === 0 ? 0 : 0.15 + 0.85 * (count / max));

    const peak = cells.find(c => c.count === max && max > 0);
    const peakLabel = peak
        ? `busiest ${DAY_LABELS[peak.dayOfWeek - 1]} at ${String(peak.hour).padStart(2, '0')}:00 with ${max} check-ins`
        : 'no check-ins in this period';

    return (
        <div data-testid="checkin-heatmap">
            {/* Exposed as a single image with a summarising label: 168 individually-focusable
                cells would be hostile to keyboard and screen-reader users, and the per-cell
                values are all available in the tables below. */}
            <div
                role="img"
                aria-label={`Check-in activity by day of week and hour — ${peakLabel}.`}
                style={{ display: 'grid', gridTemplateColumns: `auto repeat(24, 1fr)`, gap: 2 }}
            >
                {/* Hour axis */}
                <div />
                {HOURS.map(hour => (
                    <div key={`h-${hour}`} style={{ textAlign: 'center' }}>
                        <span style={{ fontSize: tokens.fontSize.xs, color: tokens.color.text.secondary }}>
                            {LABELLED_HOURS.has(hour) ? hour : ''}
                        </span>
                    </div>
                ))}

                {/* One row per day */}
                {DAY_LABELS.map((label, index) => {
                    const day = index + 1;
                    return (
                        <React.Fragment key={label}>
                            <div style={{ paddingRight: tokens.space[2], display: 'flex', alignItems: 'center' }}>
                                <span style={{ fontSize: tokens.fontSize.xs, color: tokens.color.text.secondary }}>
                                    {label}
                                </span>
                            </div>
                            {HOURS.map(hour => {
                                const count = counts.get(`${day}-${hour}`) ?? 0;
                                return (
                                    <div
                                        key={`${day}-${hour}`}
                                        title={`${label} ${String(hour).padStart(2, '0')}:00 — ${count} check-in${count === 1 ? '' : 's'}`}
                                        style={{
                                            height: 18,
                                            borderRadius: tokens.radius.sm,
                                            backgroundColor: count === 0 ? tokens.color.border.default : tokens.color.primary,
                                            opacity: count === 0 ? 0.35 : intensity(count)
                                        }}
                                    />
                                );
                            })}
                        </React.Fragment>
                    );
                })}
            </div>

            {/* Legend */}
            <div style={{ display: 'flex', alignItems: 'center', gap: tokens.space[2], marginTop: tokens.space[3] }}>
                <Text variant="caption">Quieter</Text>
                {[0.15, 0.4, 0.65, 1].map(step => (
                    <div
                        key={step}
                        style={{
                            width: 18,
                            height: 12,
                            borderRadius: tokens.radius.sm,
                            backgroundColor: tokens.color.primary,
                            opacity: step
                        }}
                    />
                ))}
                <Text variant="caption">Busier{max > 0 ? ` (peak ${max})` : ''}</Text>
            </div>
        </div>
    );
};

export default CheckinHeatmap;
